import Icon from '@ant-design/icons';
import { generatePath } from '@remix-run/router';
import type { InputRef, RadioChangeEvent } from 'antd';
import { Collapse, Input, Radio, Space, message } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';

import InstagramSvg from '@/components/Icon/svgs/Instagram.svg';
import TelegramSvg from '@/components/Icon/svgs/Telegram.svg';
import WhatsAppSvg from '@/components/Icon/svgs/WhatsApp.svg';
import XSvg from '@/components/Icon/svgs/X.svg';
import FacebookSvg from '@/components/Icon/svgs/facebook.svg';
import PlaneSvg from '@/components/Icon/svgs/go-to.svg';
// import KeySvg from '@/components/Icon/svgs/key.svg';
import LinkSvg from '@/components/Icon/svgs/link_share.svg';
// import LockSvg from '@/components/Icon/svgs/lock_fill.svg';
import RightSvg from '@/components/Icon/svgs/right_share.svg';
import MindverseModal, { SizeType } from '@/components/modal';
import { useModel } from '@/hooks/useModel';
import { useTranslation } from '@/i18n/client';
import {
  type INote,
  type INote_List,
  generateShareToken,
  updateNoteAccess,
  updateShareAccessPwd
} from '@/service/note';
import reportClick, { EReportClickKey } from '@/utils/report/reportClick';
import { ROUTER_PATH } from '@/utils/router';
import { copyToClipboard } from '@/utils/utils';

import Style from './index.less';

interface IProps {
  onClose: () => void;
  open: boolean;
  detail: INote | INote_List;
  mindId: string;
}

const ExpandIcon = (active: any) => {
  return (
    <Icon
      className="!text-[20px]"
      component={RightSvg}
      style={{
        transform: active.isActive ? 'rotate(90deg)' : 'rotate(0deg)',
        transition: 'transform 0.3s'
      }}
    />
  );
};

export default function ShareModal(props: IProps): JSX.Element {
  const { onClose, open, detail, mindId } = props;
  const [shareStatus, setShareStatus] = useState<boolean>(false);
  // const [lock, setLock] = useState<boolean>(false);
  // const [lockLoading, setLockLoading] = useState<boolean>(false);
  const [psw, setPsw] = useState<string | undefined>(undefined);
  const [publicStatus, setPublicStatus] = useState<boolean>(false);
  const [value, setValue] = useState<string>('');
  const [showChangePsw, setShowChangePsw] = useState<boolean>(false);
  const [pswLoading, setPswLoading] = useState<boolean>(false);
  const inputRef = useRef<InputRef>(null);
  const [errorTips, setErrorTips] = useState<string>('');
  const { userProfile } = useModel('auth-model', (model) => ({
    userProfile: model.userProfile
  }));
  const { t } = useTranslation();

  useEffect(() => {
    const _detail = detail as INote_List;

    setShareStatus(_detail.shareStatus);
    setPublicStatus(_detail.sharePublicStatus || false);
    // setLock(_detail.shareNeedPwdStatus || false);
    setPsw(_detail.sharePwd);

    if (_detail.shareStatus) {
      if (_detail.sharePublicStatus) {
        setValue('public');

        return;
      }

      setValue('link');

      return;
    }

    setValue('private');
  }, [detail]);

  const onChange = (e: RadioChangeEvent) => {
    const v = e.target.value;
    const _detail = detail as INote_List;

    let share = false;
    let pub = false;

    setValue(v);

    switch (v) {
      case 'private':
        setShareStatus(false);
        setPublicStatus(false);
        share = false;
        pub = false;

        break;
      case 'public':
        setPsw(undefined);
        // setLock(false);
        setShareStatus(true);
        setPublicStatus(true);
        share = true;
        pub = true;

        break;
      case 'link':
        setShareStatus(true);
        setPublicStatus(false);
        share = true;
        pub = false;

        break;
    }

    if (!_detail.noteId) {
      message.error(t('error.oops'));

      return;
    }

    shareChange(share, pub);
  };
  const shareChange = useCallback(
    (share: boolean, pub: boolean) => {
      const _detail = detail as INote_List;

      return updateNoteAccess({
        noteId: _detail.noteId!,
        mindId: mindId || _detail.mindId,
        open: share,
        openPublic: pub
      })
        .then((res) => {
          if (res.data.code !== 0) {
            throw new Error(res.data.message || t('memoryDetail.shareModal.updateFailed'));
          }

          message.success(t('memoryDetail.shareModal.updateSuccess'));
        })
        .catch((err) => {
          if (err.name !== 'AxiosError') message.error(err.message);
        });
    },
    [detail, mindId]
  );

  // const handleLockSwitch = useCallback(
  //   (v: boolean, e?: React.MouseEvent<HTMLButtonElement>) => {
  //     if (e) {
  //       e.stopPropagation();
  //     }

  //     const _detail = detail as INote_List;

  //     if (!_detail.noteId) {
  //       message.error(t('error.oops'));

  //       return;
  //     }

  //     setLockLoading(true);
  //     setLock(v);

  //     updateShareAccessPwd({ noteId: _detail.noteId, mindId: mindId || _detail.mindId, open: v })
  //       .then((res) => {
  //         if (res.data.code !== 0) {
  //           throw new Error(res.data.message || t('memoryDetail.shareModal.updateFailed'));
  //         }

  //         if (v) {
  //           setPsw(res.data.data);
  //         } else {
  //           message.success(t('memoryDetail.shareModal.updateSuccess'));
  //         }
  //       })
  //       .catch((err) => {
  //         if (err.name !== 'AxiosError') message.error(err.message);

  //         setLock(!v);
  //       })
  //       .finally(() => {
  //         setLockLoading(false);
  //       });
  //   },
  //   [detail, mindId]
  // );

  const handleChangePsw = (_value: string) => {
    const _detail = detail as INote_List;

    if (!_detail.noteId) {
      message.error(t('error.oops'));

      return;
    }

    const regex = /^.{6}$/;

    if (_value && regex.test(_value)) {
      setErrorTips('');
    } else {
      setErrorTips(t('memoryDetail.shareModal.pleaseEnter6DigitCode'));

      return;
    }

    setPswLoading(true);
    updateShareAccessPwd({
      noteId: _detail.noteId,
      mindId: mindId || _detail.mindId,
      open: true,
      password: _value
    })
      .then((res) => {
        if (res.data.code !== 0) {
          throw new Error(res.data.message || t('memoryDetail.shareModal.updateFailed'));
        }

        setPsw(_value);
        setShowChangePsw(false);
        message.success(t('memoryDetail.shareModal.updateSuccess'));
      })
      .catch((err) => {
        message.error(err.message);
      })
      .finally(() => {
        setPswLoading(false);
      });
  };

  const handlePublish = async () => {
    if (value !== 'public') {
      setValue('public');
      setShareStatus(true);
      setPublicStatus(true);
      // setLock(false);
      await shareChange(true, true);
    }

    if (!detail.noteId) {
      message.error(t('error.oops'));

      return;
    }

    try {
      const res = await generateShareToken({ noteId: detail.noteId, mindId: mindId ?? '' });

      if (res.data.code !== 0) {
        throw new Error(res.data.message || t('memoryDetail.shareModal.generateLinkFailed'));
      }

      const url = `${window.origin}${generatePath(
        value === 'public' ? ROUTER_PATH.public : ROUTER_PATH.memory,
        {
          noteId: res.data.data
        }
      )}`;

      const copyContent = decodeURIComponent(
        t('memoryDetail.shareModal.copyContent2', {
          username: userProfile?.name || 'Second Me',
          url: encodeURIComponent(url)
        })
      );

      await copyToClipboard(copyContent);

      message.success(
        <span
          dangerouslySetInnerHTML={{
            __html: t('memoryDetail.shareModal.copySuccessTips', {
              url
            })
          }}
        />
      );

      reportClick(EReportClickKey.memory_share_clicked, { key1: detail.noteId });
    } catch (e) {
      message.error(t('memoryDetail.shareModal.copyLinkFailed'));
    }
  };

  const handleCopyNoteShareLink = async () => {
    if (value === 'private') {
      setValue('link');
      setShareStatus(true);
      setPublicStatus(false);
      await shareChange(true, false);
    }

    if (!detail.noteId) {
      message.error(t('error.oops'));

      return;
    }

    try {
      const res = await generateShareToken({ noteId: detail.noteId, mindId: mindId ?? '' });

      if (res.data.code !== 0) {
        throw new Error(res.data.message || t('memoryDetail.shareModal.generateLinkFailed'));
      }

      const url = `${window.origin}${generatePath(
        value === 'public' ? ROUTER_PATH.public : ROUTER_PATH.memory,
        {
          noteId: res.data.data
        }
      )}`;

      let copyContent = decodeURIComponent(
        t(
          value === 'public'
            ? 'memoryDetail.shareModal.copyContent2'
            : 'memoryDetail.shareModal.copyContent',
          {
            username: userProfile?.name || 'Second Me',
            url: encodeURIComponent(url)
          }
        )
      );

      if (psw) {
        copyContent += t('memoryDetail.shareModal.passwordContent', { psw });
      }

      await copyToClipboard(copyContent);

      message.success(
        <span
          dangerouslySetInnerHTML={{
            __html: t('memoryDetail.shareModal.copySuccessTips', {
              url
            })
          }}
        />
      );

      reportClick(EReportClickKey.memory_share_clicked, { key1: detail.noteId });
    } catch (e) {
      message.error(t('memoryDetail.shareModal.copyLinkFailed'));
    }
  };
  const items = [
    {
      key: '1',
      label: (
        <div className="flex flex-col items-start justify-center">
          <div className="text-primary-black text-sm leading-6">
            {shareStatus
              ? publicStatus
                ? t('memoryDetail.shareModal.public')
                : t('memoryDetail.shareModal.link')
              : t('memoryDetail.shareModal.private')}
          </div>
          <div className="text-[#ACABB3] text-xs leading-5">
            {shareStatus
              ? publicStatus
                ? t('memoryDetail.shareModal.publicDesc')
                : t('memoryDetail.shareModal.linkSharingDesc')
              : t('memoryDetail.shareModal.privateDesc')}
          </div>
        </div>
      ),
      children: (
        <div className="flex flex-col gap-4">
          <Radio.Group onChange={onChange} value={value}>
            <Space direction="vertical">
              <Radio value={'private'}>
                <div className="flex flex-col items-start justify-center">
                  <div className="text-primary-black text-sm leading-6">
                    {t('memoryDetail.shareModal.private')}
                  </div>
                  <div className="text-[#ACABB3] text-xs leading-5">
                    {t('memoryDetail.shareModal.privateDesc')}
                  </div>
                </div>
              </Radio>
              <Radio value={'link'}>
                <div className="flex flex-col items-start justify-center">
                  <div className="text-primary-black text-sm leading-6">
                    {t('memoryDetail.shareModal.linkSharing')}
                  </div>
                  <div className="text-[#ACABB3] text-xs leading-5">
                    {t('memoryDetail.shareModal.linkSharingDesc')}
                  </div>
                </div>
              </Radio>
              <Radio value={'public'}>
                <div className="flex flex-col items-start justify-center">
                  <div className="text-primary-black text-sm leading-6">
                    {t('memoryDetail.shareModal.public')}
                  </div>
                  <div className="text-[#ACABB3] text-xs leading-5">
                    {t('memoryDetail.shareModal.publicDesc')}
                  </div>
                </div>
              </Radio>
            </Space>
          </Radio.Group>
          {/* {shareStatus && !publicStatus ? (
            <div className="flex flex-col">
              <div className="flex items-center py-1 gap-2">
                <Icon className="text-[20px]" component={LockSvg} />
                <span className="text-primary-black text-sm leading-6">
                  {t('memoryDetail.shareModal.password')}
                </span>
                <Switch
                  checked={lock}
                  className="w-8"
                  loading={lockLoading}
                  onClick={handleLockSwitch}
                  size="small"
                />
              </div>
              {lock && !lockLoading ? (
                <div
                  className="flex items-center gap-2 py-1"
                  onClick={() => {
                    psw
                      ? copyToClipboard(psw).then(() => {
                          message.success(t('common.copied'));
                        })
                      : null;
                  }}
                >
                  <Icon className="text-[20px]" component={KeySvg} />
                  <div className="text-primary-black text-sm leading-6 cursor-pointer hover:text-primary-sub-color transition-colors">
                    {psw}
                  </div>
                  <div
                    className="text-primary-sub-color  text-sm leading-6 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowChangePsw(true);
                    }}
                  >
                    {t('common.edit')}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null} */}
        </div>
      )
    }
  ];

  return (
    <>
      <MindverseModal
        destroyOnClose
        footer={null}
        onCancel={() => onClose()}
        open={open}
        size={SizeType.normal}
        title={t('memoryDetail.shareModal.share')}
      >
        <Collapse
          className={Style.collapse}
          defaultActiveKey={value === 'private' ? 1 : 0}
          expandIcon={ExpandIcon}
          expandIconPosition="end"
          ghost
          items={items}
        />
        <div className="pt-4 px-2 flex items-center gap-6 border-0 border-t border-solid border-[#F0EBE5]">
          <div
            className="flex flex-col items-center justify-center gap-1 cursor-pointer group"
            onClick={handleCopyNoteShareLink}
          >
            <div className="w-[52px] h-[52px] rounded-full bg-[#F0EBE5] flex items-center justify-center">
              <div className="absolute w-[52px] h-[52px] rounded-full bg-[#160a0a20] opacity-0 group-hover:opacity-100 transition-opacity" />
              <Icon className="text-xl font-medium" component={LinkSvg} />
            </div>
            <div className="text-sm leading-6">{t('memoryDetail.shareModal.copyLink')}</div>
          </div>
          <div
            className="flex flex-col items-center justify-center gap-1 cursor-pointer group"
            onClick={handlePublish}
          >
            <div className="w-[52px] h-[52px] rounded-full bg-[#F0EBE5] flex items-center justify-center">
              <div className="absolute w-[52px] h-[52px] rounded-full bg-[#160a0a20] opacity-0 group-hover:opacity-100 transition-opacity" />
              <Icon className="text-xl font-medium" component={PlaneSvg} />
            </div>
            <div className="text-sm leading-6">{t('memoryDetail.shareModal.publish')}</div>
          </div>
          <div className="flex flex-col items-center justify-center gap-1 cursor-pointer hidden">
            <div className="w-[52px] h-[52px] rounded-full bg-[#32D851] flex items-center justify-center">
              <Icon className="text-xl font-medium text-primary-white" component={WhatsAppSvg} />
            </div>
            <div className="text-sm leading-6">{t('memoryDetail.shareModal.whatsApp')}</div>
          </div>
          <div className="flex flex-col items-center justify-center gap-1 cursor-pointer hidden">
            <div className="w-[52px] h-[52px] rounded-full bg-[#54A6E4] flex items-center justify-center">
              <Icon className="text-xl font-medium text-primary-white" component={TelegramSvg} />
            </div>
            <div className="text-sm leading-6">{t('memoryDetail.shareModal.telegram')}</div>
          </div>
          <div className="flex flex-col items-center justify-center gap-1 cursor-pointer hidden">
            <div className="w-[52px] h-[52px] rounded-full bg-[#1877F2] flex items-center justify-center">
              <Icon className="text-xl font-medium text-primary-white" component={FacebookSvg} />
            </div>
            <div className="text-sm leading-6">{t('memoryDetail.shareModal.facebook')}</div>
          </div>
          <div className="flex flex-col items-center justify-center gap-1 cursor-pointer hidden">
            <div
              className="w-[52px] h-[52px] rounded-full flex items-center justify-center"
              style={{
                background:
                  'linear-gradient(180deg, #5155D3 0%, #853BCC 14%, #EA1BB6 44%, #FF1367 74%, #FAC332 100%)'
              }}
            >
              <Icon className="text-xl font-medium text-primary-white" component={InstagramSvg} />
            </div>
            <div className="text-sm leading-6">{t('memoryDetail.shareModal.instagram')}</div>
          </div>
          <div className="flex flex-col items-center justify-center gap-1 cursor-pointer hidden">
            <div className="w-[52px] h-[52px] rounded-full bg-[#07041A] flex items-center justify-center">
              <Icon className="text-xl font-medium text-primary-white" component={XSvg} />
            </div>
            <div className="text-sm leading-6">{t('memoryDetail.shareModal.x')}</div>
          </div>
        </div>
      </MindverseModal>

      <MindverseModal
        cancelText={t('common.cancel')}
        confirmLoading={pswLoading}
        onCancel={() => {
          setShowChangePsw(false);
        }}
        onOk={() => {
          handleChangePsw(inputRef.current?.input?.value || '');
        }}
        open={showChangePsw}
        size={SizeType.normal}
        title={t('memoryDetail.shareModal.editPassword')}
      >
        <Input ref={inputRef} defaultValue={psw} />
        {errorTips ? <span className={'text-[#FF7875] text-xs leading-5'}>{errorTips}</span> : null}
      </MindverseModal>
    </>
  );
}
